import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

// `apiNest` ya trae el prefijo de version (/v350 en dev), asi que aca no se repite.
const BASE = `${environment.apiNest}/registro-cliente`;

// ------------------------------------------------------------------ catalogos

export interface TipoDocumento {
  id: number;
  descripcion: string;
  abreviatura: string;
  /** Cantidad EXACTA de caracteres; null = el tipo no tiene largo fijo (pasaporte, CE). */
  longitud: number | null;
}

export interface PaisCelular {
  /** Codigo telefonico como entero (51), que es lo que espera el backend. */
  codigo: number;
  iso: string;
  nombre: string;
  /** Digitos del numero local, sin el codigo de pais. */
  longitud: number;
}

export interface CatalogosRegistro {
  tiposDocumento: TipoDocumento[];
  paisesCelular: PaisCelular[];
}

// ------------------------------------------------------------------ pasos

export interface ValidarDocumentoRequest {
  idTipoDocumento: number;
  numeroDocumento: string;
  aceptaTerminos: boolean;
  versionTerminos?: string;
}

/** Alta normal: el documento no existe, hay que pedirle los datos de contacto. */
export interface DocumentoNuevoResponse {
  modo: 'nuevo';
  disponible: true;
  tipoDocumento: string;
  siguientePaso: 'contacto';
}

/**
 * Reclamo de cuenta: el paciente ya esta en la ficha de la clinica pero nunca se dio de
 * alta (o abandono este mismo flujo despues del paso 2). El backend ya devuelve el token
 * de registro, asi que el wizard salta el paso de contacto.
 */
export interface DocumentoReclamoResponse {
  modo: 'reclamo';
  disponible: false;
  tipoDocumento: string;
  /** "+51 ******446". El celular NO se puede cambiar en este modo, por eso solo se muestra. */
  celularEnmascarado: string;
  token: string;
  siguientePaso: 'codigo';
}

export type ValidarDocumentoResponse = DocumentoNuevoResponse | DocumentoReclamoResponse;

export interface RegistrarContactoRequest extends ValidarDocumentoRequest {
  nombres: string;
  apellidos?: string;
  email: string;
  emailConfirmacion: string;
  /** Entero (51), no "+51" ni string. */
  paisCelular: number;
  /** Solo digitos, sin espacios ni guiones. */
  celular: string;
  /** Codigo de quien lo invito (opcional). Uno que no existe da 400 antes de crear nada. SPEC-036. */
  codigoReferido?: string;
}

export interface RegistrarContactoResponse {
  idCliente: number;
  token: string;
  siguientePaso: 'codigo';
}

export interface EnviarCodigoResponse {
  enviado: boolean;
  /** 'log-dev' cuando el gateway de WhatsApp no existe (solo en desarrollo). */
  canal?: string;
  expiraEnMinutos: number;
}

export interface VerificarCodigoResponse {
  verificado: true;
  /** Token NUEVO, habilitado para el paso de la clave. Reemplaza al anterior. */
  token: string;
}

/**
 * El paso final devuelve la sesion ya armada: no hace falta un /login extra despues de
 * fijar la clave. Es el mismo access_token que emite /login.
 */
export interface DefinirClaveResponse {
  listo: true;
  idCliente: number;
  nombres: string;
  access_token: string;
}

export interface LoginClienteResponse {
  idCliente: number;
  nombres: string;
  access_token: string;
}

/**
 * Registro de cliente en 4 pasos (documento -> contacto -> codigo -> clave) y login por
 * documento. REST contra api-nest (SPEC-030). Es la unica auth del portal que trabaja
 * sobre dbo.Cliente; la del marketplace (MktUsuario) vive en MarketplaceAuthService.
 *
 * El token de registro NO se persiste: vive 20 minutos y es una credencial a medio
 * camino (habilita pedir codigo y poner clave, nada mas). Guardarlo en localStorage lo
 * dejaria sobrevivir al cierre de la pestana sin que nadie termine el registro, asi que
 * se queda en memoria de este servicio y muere con el reload.
 */
@Injectable({ providedIn: 'root' })
export class RegistroClienteService {
  private token: string | null = null;

  constructor(private http: HttpClient) {}

  /** Header propio del flujo: NO es un Bearer, el backend lo lee de x-registro-token. */
  private get headers(): HttpHeaders {
    return new HttpHeaders({ 'x-registro-token': this.token ?? '' });
  }

  get tieneToken(): boolean {
    return !!this.token;
  }

  /** Se llama al abrir el wizard y al reiniciarlo: nunca arrastrar un token de otro intento. */
  reset(): void {
    this.token = null;
  }

  catalogos(): Observable<CatalogosRegistro> {
    return this.http.get<CatalogosRegistro>(`${BASE}/catalogos`);
  }

  /**
   * Paso 1. En modo "reclamo" la respuesta ya trae el token, porque ese cliente ya existe
   * en la base y no hay paso 2 que lo cree.
   */
  validarDocumento(dto: ValidarDocumentoRequest): Observable<ValidarDocumentoResponse> {
    return this.http.post<ValidarDocumentoResponse>(`${BASE}/documento`, dto).pipe(
      tap((r) => {
        if (r.modo === 'reclamo') this.token = r.token;
      }),
    );
  }

  /** Paso 2 (solo modo "nuevo"). Crea el Cliente y entrega el token de registro. */
  registrarContacto(dto: RegistrarContactoRequest): Observable<RegistrarContactoResponse> {
    return this.http
      .post<RegistrarContactoResponse>(`${BASE}/contacto`, dto)
      .pipe(tap((r) => (this.token = r.token)));
  }

  /** Paso 3 — envio del codigo por WhatsApp. Limitado a 3 cada 5 minutos (429). */
  enviarCodigo(): Observable<EnviarCodigoResponse> {
    return this.http.post<EnviarCodigoResponse>(`${BASE}/codigo`, null, { headers: this.headers });
  }

  /**
   * Paso 3 bis. El token que vuelve REEMPLAZA al que teniamos: es el mismo cliente pero
   * con verificado=true en el payload, y el paso de la clave rechaza (401) cualquier
   * token que no lo tenga. Mandar el viejo al paso 4 es el error facil de cometer aca.
   */
  verificarCodigo(codigo: string): Observable<VerificarCodigoResponse> {
    return this.http
      .post<VerificarCodigoResponse>(`${BASE}/verificar`, { codigo }, { headers: this.headers })
      .pipe(tap((r) => (this.token = r.token)));
  }

  /**
   * Paso 4 — contrasena. Exige el token ya verificado del paso anterior y devuelve la
   * sesion definitiva (access_token), asi que el wizard NO tiene que loguear aparte.
   */
  definirClave(clave: string, claveConfirmacion: string): Observable<DefinirClaveResponse> {
    return this.http.post<DefinirClaveResponse>(
      `${BASE}/clave`,
      { clave, claveConfirmacion },
      { headers: this.headers },
    );
  }

  /** Login por DOCUMENTO (no por correo: media base de clientes no tiene email cargado). */
  login(idTipoDocumento: number, numeroDocumento: string, clave: string): Observable<LoginClienteResponse> {
    return this.http.post<LoginClienteResponse>(`${BASE}/login`, {
      idTipoDocumento,
      numeroDocumento,
      clave,
    });
  }
}
